<template>
  <div
    v-if="marketingCookie"
    class="paypal-message-wrapper mt-2"
  >
    <paypal-message
      v-if="marketingCookie"
      ref="element"
    />
  </div>
</template>

<script setup lang="ts">
import type { Price, PromotionPrice } from '@expert/types';

interface Props {
  articleId: string;
  price: Price;
  promotionPrice?: PromotionPrice;
  pageType: string;
}

const props = defineProps<Props>();

const marketingCookie = getCookiePermissions(PermissionMapping.MARKETING);
const element = ref();

const pdsShoppingCartPrice = computed(() => {
  const activeBundlePrice = usePDSActiveBundlePrice().value;
  const basePrice = props.promotionPrice?.checkoutPrice ?? props.price?.bruttoPrice;
  if (activeBundlePrice && activeBundlePrice > 0) {
    return basePrice + activeBundlePrice;
  } else {
    return basePrice;
  }
});

const loadScript = (): void => {
  if (!usePaypalScriptInjected().value && marketingCookie.value) {
    usePaypalScriptInjected().value = true;
    useHead({
      script: [
        { src: 'https://www.paypal.com/web-sdk/v6/core', tagPosition: 'head', async: true },
        { src: 'https://www.paypal.com/web-sdk/v6/paypal-messages', tagPosition: 'head', async: true },
      ],
    });
  }
};

const waitForPaypal = async (): Promise<void> => {
  if (!window.paypal) {
    await wait(100);
    await waitForPaypal();
  }
};

const getMessagesInstance = async (): Promise<{ fetchContent: (options: object) => void }> => {
  await waitForPaypal();
  return await usePaypalMessagesInstance(window.paypal, props.pageType, isZeroFunding(props.articleId)).value;
};

const load = async (): Promise<void> => {
  const instance = await getMessagesInstance();
  instance.fetchContent({
    amount: pdsShoppingCartPrice.value.toString(),
    currencyCode: 'EUR',
    onReady: (content) => {
      element.value.setContent(content);
    },
  });
};

onMounted(() => {
  watch(marketingCookie, loadScript, { immediate: true });
  watch(pdsShoppingCartPrice, load, { immediate: true });
});
</script>

<style lang="css" scoped>
.paypal-message-wrapper {
  min-height: 67px;
}

.article-list-item .paypal-message-wrapper {
  min-height: 0;
}
</style>
